import {
    IndeedApplyButton,
    type IndeedApplyButtonProps,
    type JobModel,
    type ViewJobDetailsModel,
} from './IndeedApplyButton';
import { Box } from '../../../../../../mads';
import { useI18n } from '../../../../../../shared/I18n';
import { useLogErrorOnce } from '../../../../../../shared/logging/useLogErrorOnce';
import { navigateView } from '../../../../../native-module/navigateView';
import { useViewjobProctorAllocation } from '../../../../../proctor/__generated__/ViewjobProctorContext';
import { IaBrandedApplyButtonRnTst } from '../../../../../proctor/__generated__/ViewjobProctorSpecifications';
import {
    useApplyLinkLoading,
    useApplyLinkUrl,
    useDomainUrl,
    useViewjobOneGraphModel,
    useViewjobSourceUrl,
} from '../../../../context/ViewJobDataContext';
import { useGFJAutoClickApply } from '../../../../hooks/useGFJAutoClickApply';
import { useGetInterviewNowIndeedApplyButtonProps } from '../InterviewNow/useGetInterviewNowIndeedApplyButtonProps';

import React, { useCallback } from 'react';
import { ActivityIndicator, Platform, StyleSheet } from 'react-native';

type OGWrapperOmitKeys =
    | 'indeedApplyButtonContainer'
    | 'jobModel'
    | 'viewJobDetailsModel'
    | 'domain';

export type OGWrappedIndeedApplyButtonProps = Omit<IndeedApplyButtonProps, OGWrapperOmitKeys>

export function OGWrappedIndeedApplyButton({
    variant,
    isSecondaryApplyButton,
    hasLoggedImpression,
    hasLoggedSeen,
    setHasLoggedSeen,
    buttonSize,
}: OGWrappedIndeedApplyButtonProps) {
    const i18n = useI18n();
    const ogModel = useViewjobOneGraphModel();
    const domain = useDomainUrl();
    const vjUrl = useViewjobSourceUrl();
    const isWeb = Platform.OS === 'web';
    const { indeedApplyButtonContainer, job } = ogModel ?? {};
    const applyLinkUrl = useApplyLinkUrl();
    const applyLinkLoading = useApplyLinkLoading();
    const getInterviewNowProps = useGetInterviewNowIndeedApplyButtonProps();
    const logErrorOnce = useLogErrorOnce();
    const applyCtaProctor = useViewjobProctorAllocation(IaBrandedApplyButtonRnTst);
    const isApplyCtaTestActive = applyCtaProctor.value === IaBrandedApplyButtonRnTst.buckets.Test;

    // Hiring events have their own indeedApplyUrl
    const isHiringEvent = !!indeedApplyButtonContainer?.indeedApplyUrl;
    // TMN apply flow is only enabled on web (mobile and desktop); on native fall back to the standard apply URL.
    const effectiveTmnUrl = isWeb ? indeedApplyButtonContainer?.tmnUrl : undefined;
    // Existing applicants on IoD jobs have a dedicated reconnect URL
    const existingApplicantUrl = !isSecondaryApplyButton
        ? (ogModel?.interviewNowModel?.existingApplicantUrl ?? null)
        : null;
    const handleIodReconnect = useCallback(() => {
        if (!existingApplicantUrl) return;
        void navigateView(vjUrl, existingApplicantUrl, existingApplicantUrl, undefined);
    }, [vjUrl, existingApplicantUrl]);
    const effectiveIndeedApplyUrl = effectiveTmnUrl
        ? effectiveTmnUrl
        : existingApplicantUrl
            ?? (isHiringEvent
                ? indeedApplyButtonContainer?.indeedApplyUrl
                : (applyLinkUrl ?? ''));

    useGFJAutoClickApply(
        effectiveIndeedApplyUrl || null,
        ogModel?.mobvjtk,
        job?.jobKey,
        ogModel?.autoClickApplyFlag,
    );

    if (ogModel == null || indeedApplyButtonContainer == null || job == null) {
        return null;
    }

    // Derive branded CTA text.
    // - Hiring events keep their server-driven contentHtml ("Schedule an interview" etc.)
    // - Test bucket: "Apply with Indeed" (plain text, all surfaces)
    const applyCtaButtonText =
        isApplyCtaTestActive && !isHiringEvent
            ? i18n.gettext('Apply with Indeed')
            : undefined;

    if (!isHiringEvent && !effectiveTmnUrl && applyLinkLoading) {
        return (
            <Box sx={styles.loadingContainer}>
                <ActivityIndicator size="small" />
            </Box>
        );
    }

    if (!effectiveIndeedApplyUrl) {
        if (!job?.isExpired) {
            logErrorOnce('Apply button missing: IndeedApply button not rendered', {
                jobKey: job?.jobKey,
                isHiringEvent,
                hasApplyLinkUrl: !!applyLinkUrl,
            });
        }
        return null;
    }

    const jobModel: JobModel = {
        key: job.jobKey,
        title: job.details?.title,
    };

    const viewJobDetailsModel: ViewJobDetailsModel = {
        mobvjtk: ogModel.mobvjtk,
        jobCountry: ogModel.job.jobCountry,
    };

    const effectiveIndeedApplyButtonContainer = {
        ...indeedApplyButtonContainer,
        indeedApplyUrl: effectiveIndeedApplyUrl,
        tmnUrl: effectiveTmnUrl,
    };

    return (
        <IndeedApplyButton
            indeedApplyButtonContainer={effectiveIndeedApplyButtonContainer}
            jobModel={jobModel}
            viewJobDetailsModel={viewJobDetailsModel}
            domain={domain}
            variant={variant}
            hasLoggedImpression={hasLoggedImpression}
            hasLoggedSeen={hasLoggedSeen}
            setHasLoggedSeen={setHasLoggedSeen}
            buttonSize={buttonSize}
            buttonText={applyCtaButtonText}
            {...getInterviewNowProps(isSecondaryApplyButton)}
            {...(existingApplicantUrl != null ? {
                skipNativeApplyBridge: true,
                onClick: handleIodReconnect,
            } : null)}
        />
    );
}

const styles = StyleSheet.create({
    loadingContainer: {
        height: 48,
        justifyContent: 'center',
        alignItems: 'center',
    },
});
